/**
 * EV Quotes tab — quotation ledger built on top of the EV price list.
 * Standalone from ICE. Supports create/edit/status/print/WhatsApp.
 * Also hosts the read-only live Price List view (EvPriceListPanel).
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Plus, MagnifyingGlass, Printer, WhatsappLogo, PencilSimple, Trash, X, ArrowsClockwise } from "@phosphor-icons/react";
import { FinanceSchemePicker } from "@/components/FinanceSchemePicker";

const inr = (v) => `₹${Number(v || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;

const FINANCIER_FALLBACK = ["HDFC BANK LTD", "SBI BANK", "BANK OF BARODA", "ICICI", "AXIS BANK", "TVS FINANCE", "OTHER"];

const STATUS_COLORS = {
  DRAFT:     "bg-canvas-line/40 text-ink",
  BOOKED:    "bg-emerald-100 text-emerald-800",
  DELIVERED: "bg-blue-100 text-blue-800",
  CANCELLED: "bg-rose-100 text-rose-700",
};

const LINE_ITEMS = [
  ["ex_showroom_net",     "EX-SHOWROOM (After Subsidy)"],
  ["software_upgrade",    "SOFTWARE UPGRADE"],
  ["reg_charges",         "RTO CHARGES"],
  ["insurance_wo_nildip", "INSURANCE (W/O NIL DIP)"],
  ["fac",                 "HANDLING CHARGES"],
  ["number_plate_bracket","NUMBER PLATE BRACKET"],
  ["hsrp_plates",         "HSRP NO. PLATES"],
  ["pillion_footrest",    "PILLION SIDE FOOTREST"],
  ["all_round_guard",     "ALL ROUND GUARD"],
  ["centre_stand",        "CENTRE STAND"],
  ["floor_mat",           "IQUBE DESIGNER FLOOR MAT"],
  ["helmet",              "HELMET"],
  ["life_tax",            "LIFE TAX"],
  ["zero_dep_addon",      "INSURANCE ZERO DEP ADD ON"],
  ["hypothecation",       "HYPOTHECATION"],
  ["extended_warranty",   "EXTENDED WARRANTY", "ext_warranty_2y"],
];

export function EvQuotesTab({ prefillRef, onPrefillHandled }) {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(false);

  // "Quote" shortcut from the Price List view — open the New Quote drawer
  // pre-filled with the model the rep clicked.
  useEffect(() => {
    if (prefillRef) {
      setEditing({ vehicle_ref_id: prefillRef.id, vehicle_model: prefillRef.package_name });
      onPrefillHandled && onPrefillHandled();
    }
  }, [prefillRef, onPrefillHandled]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/ev-pricing/quotes", {
        params: { q: q || undefined, status: status || undefined, limit: 200 },
      });
      setRows(data.rows || []);
      setTotal(data.total || 0);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, [q, status]);
  useEffect(() => { load(); }, [load]);

  const printPdf = async (row) => {
    try {
      const resp = await api.get(`/ev-pricing/quotes/${row.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([resp.data], { type: "application/pdf" }));
      window.open(url, "_blank");
      // give the browser a moment before revoking
      setTimeout(() => URL.revokeObjectURL(url), 30000);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const sendWa = async (row) => {
    const mobile = window.prompt("Send to WhatsApp — mobile (10 digits):", row.customer?.phone || "");
    if (!mobile) return;
    try {
      const { data } = await api.post(`/ev-pricing/quotes/${row.id}/send-whatsapp`, { mobile });
      if (data.ok) toast.success("WhatsApp sent");
      else toast.error(`WhatsApp failed: ${data.vendor || ""}`);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const changeStatus = async (row, next) => {
    if (!window.confirm(`Change status of ${row.serial} to ${next}?`)) return;
    try {
      await api.post(`/ev-pricing/quotes/${row.id}/status`, { status: next });
      toast.success(`Marked ${next}`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const del = async (row) => {
    if (!window.confirm(`Delete quote ${row.serial}?`)) return;
    try {
      await api.delete(`/ev-pricing/quotes/${row.id}`);
      toast.success("Deleted"); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[240px]">
          <MagnifyingGlass size={12} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-muted" />
          <input value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Search serial / name / phone / model…"
            data-testid="evq-search"
            className="w-full pl-8 pr-3 py-2 bg-canvas border border-canvas-line text-sm focus:outline-none focus:border-ink" />
        </div>
        <select value={status} onChange={(e) => setStatus(e.target.value)}
          data-testid="evq-status-filter"
          className="px-3 py-2 bg-canvas border border-canvas-line text-sm">
          <option value="">All statuses</option>
          <option value="DRAFT">Draft</option>
          <option value="BOOKED">Booked</option>
          <option value="DELIVERED">Delivered</option>
          <option value="CANCELLED">Cancelled</option>
        </select>
        <button onClick={() => setEditing({})} data-testid="evq-new"
          className="px-3 py-2 bg-ink text-white text-xs font-mono uppercase tracking-wider hover:bg-brand flex items-center gap-1">
          <Plus size={12} weight="bold" /> New Quote
        </button>
        <div className="ml-auto text-xs font-mono text-ink-muted">
          {loading ? "Loading…" : `${rows.length} of ${total}`}
        </div>
      </div>

      <div className="border border-canvas-line overflow-x-auto max-h-[65vh] overflow-y-auto">
        <table className="w-full text-sm">
          <thead className="bg-canvas-panel border-b border-canvas-line sticky top-0">
            <tr>
              {["Serial","Date","Customer","Phone","Model","Branch","Net","Status",""].map((h) => (
                <th key={h} className="px-3 py-2 font-mono text-[10px] uppercase text-left whitespace-nowrap">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr><td colSpan={9} className="px-3 py-8 text-center text-ink-muted text-xs">No quotes found.</td></tr>
            ) : rows.map((r) => (
              <tr key={r.id} className="border-b border-canvas-line/50 hover:bg-canvas-panel/40" data-testid={`evq-row-${r.id}`}>
                <td className="px-3 py-2 font-mono text-xs font-semibold">{r.serial}</td>
                <td className="px-3 py-2 font-mono text-[11px]">{r.quote_date}</td>
                <td className="px-3 py-2 text-xs">{r.customer?.name || "-"}</td>
                <td className="px-3 py-2 font-mono text-[11px]">{r.customer?.phone || "-"}</td>
                <td className="px-3 py-2 text-xs">{r.vehicle_model}</td>
                <td className="px-3 py-2 font-mono text-[10px]">{r.branch_name || "-"}</td>
                <td className="px-3 py-2 font-mono text-xs text-right">{inr(r.totals?.net_total)}</td>
                <td className="px-3 py-2">
                  <span className={`px-1.5 py-0.5 text-[9px] font-mono uppercase tracking-wider ${STATUS_COLORS[r.status] || ""}`}>
                    {r.status}
                  </span>
                </td>
                <td className="px-3 py-2 whitespace-nowrap">
                  <button onClick={() => printPdf(r)} title="Print PDF" data-testid={`evq-print-${r.id}`}
                    className="text-ink-muted hover:text-ink p-1"><Printer size={12} /></button>
                  <button onClick={() => sendWa(r)} title="Send WhatsApp" data-testid={`evq-wa-${r.id}`}
                    className="text-emerald-700 hover:text-emerald-800 p-1"><WhatsappLogo size={12} weight="bold" /></button>
                  {r.status === "DRAFT" && (
                    <>
                      <button onClick={() => setEditing(r)} title="Edit"
                        className="text-ink-muted hover:text-ink p-1"><PencilSimple size={12} /></button>
                      <button onClick={() => changeStatus(r, "BOOKED")} className="text-[10px] font-mono uppercase text-emerald-700 hover:underline px-1">Book</button>
                    </>
                  )}
                  {r.status === "BOOKED" && (
                    <button onClick={() => changeStatus(r, "DELIVERED")} className="text-[10px] font-mono uppercase text-blue-700 hover:underline px-1">Deliver</button>
                  )}
                  {r.status !== "CANCELLED" && r.status !== "DELIVERED" && (
                    <button onClick={() => changeStatus(r, "CANCELLED")} className="text-[10px] font-mono uppercase text-rose-700 hover:underline px-1">Cancel</button>
                  )}
                  <button onClick={() => del(r)} title="Delete" className="text-rose-700 hover:bg-rose-50 p-1"><Trash size={12} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && (
        <EvQuoteEditor
          initial={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}


function EvQuoteEditor({ initial, onClose, onSaved }) {
  const isNew = !initial.id;
  const { user } = useAuth();
  const [pricelist, setPricelist] = useState([]);
  const [plSearch, setPlSearch] = useState("");
  const [plCategory, setPlCategory] = useState("");
  const today = new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState(() => ({
    quote_date: initial.quote_date || today,
    valid_until: initial.valid_until || "",
    branch_name: initial.branch_name || "",
    branch_phone: initial.branch_phone || "",
    branch_address: initial.branch_address || "",
    customer: initial.customer || { name: "", phone: "", city: "", address: "" },
    finance: initial.finance || { is_financed: false, financier: "", extended_warranty_2y: false },
    vehicle_ref_id: initial.vehicle_ref_id || "",
    vehicle_model: initial.vehicle_model || "",
    vehicle_colour: initial.vehicle_colour || "",
    items: initial.items || {},
    discount: initial.discount || 0,
    notes: initial.notes || "",
    finance_quote: initial.finance_quote?.enabled
      ? { enabled: true, scheme_id: initial.finance_quote.scheme?.id || "",
          down_payment: String(initial.finance_quote.computed?.down_payment ?? "") }
      : { enabled: false, scheme_id: "", down_payment: "" },
  }));
  const [saving, setSaving] = useState(false);
  const [branches, setBranches] = useState([]);      // live from Branch master
  const [financiers, setFinanciers] = useState(FINANCIER_FALLBACK);
  const [finSchemes, setFinSchemes] = useState([]);  // finance schemes (EMI quotes)

  useEffect(() => {
    (async () => {
      try {
        const [{ data: brs }, { data: fins }, { data: schs }] = await Promise.all([
          api.get("/ev-pricing/quotes/quote-branches"),
          api.get("/ev-pricing/quotes/quote-financiers"),
          api.get("/finance/schemes"),
        ]);
        setBranches((brs || []).filter((b) => b.active !== false));
        const names = (fins || []).filter(Boolean);
        if (names.length) setFinanciers(names);
        setFinSchemes((schs || []).filter((s) => s.active !== false));

        // Auto-select the logged-in staff's own branch (new quotes only)
        if (!initial.id && user?.branch_id && !form.branch_name) {
          const mine = (brs || []).find((b) => b.id === user.branch_id);
          if (mine) {
            const nm = (mine.name || "").toUpperCase();
            const phone = [mine.phone, mine.alternate_phone].filter(Boolean).join(", ");
            setForm((f) => ({ ...f, branch_name: nm,
              branch_phone: phone || f.branch_phone,
              branch_address: mine.address || f.branch_address }));
          }
        }
      } catch { /* masters optional — fallbacks remain */ }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Branch lock: branch-assigned users (staff / showroom managers) can only
  // issue quotes for their own showroom. Admin (no branch_id) keeps free pick.
  const myBranch = user?.branch_id ? branches.find((b) => b.id === user.branch_id) : null;
  const branchLocked = !!myBranch;
  useEffect(() => {
    if (myBranch && form.branch_name !== (myBranch.name || "").toUpperCase()) {
      const nm = (myBranch.name || "").toUpperCase();
      const phone = [myBranch.phone, myBranch.alternate_phone].filter(Boolean).join(", ");
      setForm((f) => ({ ...f, branch_name: nm, branch_phone: phone, branch_address: myBranch.address || f.branch_address }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [myBranch?.id]);

  // Pull the LIVE pricelist from the Pricing Engine (computed values, not
  // stored ones). Every row shows today's ex-showroom / on-road so quotes
  // always start from the current engine.
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/ev-pricing/quotes/pricelist");
        setPricelist(data || []);
      } catch (e) { toast.error(formatApiError(e)); }
    })();
  }, []);

  // If the drawer was opened from the Price List "Quote" shortcut, the
  // vehicle is pre-selected — fetch its full line items immediately.
  useEffect(() => {
    if (isNew && form.vehicle_ref_id && !form.items?.ex_showroom) {
      pickVehicle(form.vehicle_ref_id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-fill valid_until = quote_date + 7 days
  useEffect(() => {
    if (form.quote_date && !form.valid_until) {
      const d = new Date(form.quote_date);
      d.setDate(d.getDate() + 7);
      setForm((f) => ({ ...f, valid_until: d.toISOString().slice(0, 10) }));
    }
  }, [form.quote_date, form.valid_until]);

  const pickBranch = (name) => {
    const b = branches.find((x) => (x.name || "").toUpperCase() === name) ||
              branches.find((x) => (x.name || "").toUpperCase().startsWith(name));
    const phone = b ? [b.phone, b.alternate_phone].filter(Boolean).join(", ") : "";
    setForm((f) => ({
      ...f, branch_name: name,
      branch_phone: phone || f.branch_phone,
      branch_address: (b && b.address) || f.branch_address,
    }));
  };

  const pickVehicle = async (ref_id) => {
    if (!ref_id) return;
    // Fetch the full reference + computed retail from the engine so line items
    // match the pricelist row we just clicked.
    try {
      const { data } = await api.get(`/ev-pricing/quotes/reference/${ref_id}`);
      const ref = data?.reference || {};
      const it = data?.retail || {};
      setForm((f) => ({
        ...f,
        vehicle_ref_id: ref_id,
        vehicle_model: ref.package_name,
        items: {
          ...f.items,
          pre_ex_showroom: it.pre_ex_showroom || 0,
          subsidy: ref.subsidy || 0,
          ex_showroom: it.ex_showroom || 0,
          ex_showroom_net: it.ex_showroom || 0,
          software_upgrade: ref.software_upgrade || 0,
          reg_charges: ref.reg_charges || 0,
          insurance_wo_nildip: it.insurance_wo_nildip || 0,
          fac: ref.fac || 0,
          off_board_charger: ref.offboard_charger || 0,
          number_plate_bracket: ref.number_plate_bracket || 0,
          hsrp_plates: ref.hsrp_plates || 0,
          pillion_footrest: ref.pillion_footrest || 0,
          all_round_guard: ref.all_round_guard || 0,
          centre_stand: ref.centre_stand || 0,
          floor_mat: ref.floor_mat || 0,
          helmet: ref.helmet_sale_price || ref.helmet || 0,
          life_tax: 0,
          zero_dep_addon: ref.zero_dep_addon || 0,
          hypothecation: ref.hypothecation || 0,
          extended_warranty: ref.extended_warranty || 0,
          amc_4y: ref.amc_4y || 0,
          cash_back_offer: 0,
          card_based_offer: 0,
        },
      }));
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const filteredPricelist = useMemo(() => {
    const s = plSearch.trim().toLowerCase();
    let list = pricelist;
    if (plCategory) list = list.filter((p) => (p.family || "Other") === plCategory);
    if (s) list = list.filter((p) => p.package_name.toLowerCase().includes(s));
    return list;
  }, [pricelist, plSearch, plCategory]);

  const categories = useMemo(
    () => [...new Set(pricelist.map((p) => p.family || "Other"))],
    [pricelist]
  );

  // Live totals
  const totals = useMemo(() => {
    const it = form.items || {};
    const keys = ["ex_showroom_net","software_upgrade","reg_charges","insurance_wo_nildip",
      "fac","number_plate_bracket","hsrp_plates","pillion_footrest","all_round_guard",
      "centre_stand","floor_mat","helmet","life_tax","zero_dep_addon","hypothecation","extended_warranty"];
    const sub = keys.reduce((s, k) => s + Number(it[k] || 0), 0);
    const disc = Number(form.discount || 0);
    const offers = Number(it.cash_back_offer || 0) + Number(it.card_based_offer || 0);
    return { sub: Math.round(sub), net: Math.round(sub - disc), offers: Math.round(offers) };
  }, [form.items, form.discount]);

  const setItem = (k, v) => setForm((f) => ({ ...f, items: { ...(f.items || {}), [k]: v === "" ? 0 : Number(v) } }));

  // Live EMI preview — mirrors the server-side flat-rate math in
  // routers/finance_quotes.py (empty down payment = auto from scheme %).
  const finScheme = finSchemes.find((s) => s.id === form.finance_quote.scheme_id);
  const finPreview = (() => {
    if (!form.finance_quote.enabled || !finScheme || !totals.net) return null;
    const amount = totals.net;
    const auto = Math.round(amount * (finScheme.down_payment_pct || 0) * 100) / 100;
    const dp = form.finance_quote.down_payment !== "" ? Number(form.finance_quote.down_payment) : auto;
    const baseLoan = amount - dp;
    const proc = Math.round((Math.floor(baseLoan / 1000) + (baseLoan % 1000 >= 500 ? 1 : 0)) * 1000 * (finScheme.processing_charge_pct || 0));
    const charges = proc + (finScheme.stamp_duty || 0) + (finScheme.selling_charges || 0)
      + (finScheme.application_charge || 0) + (finScheme.gst || 0)
      + (finScheme.insurance || 0) + (finScheme.dealer_incentive || 0);
    // Processing costs are financed: interest runs on amount + costs - DP.
    const loan = baseLoan + charges;
    const interest = (loan * (finScheme.rate_interest || 0) / 100) * (finScheme.tenure || 0) / 12;
    const emi = finScheme.tenure ? Math.round((loan + interest) / finScheme.tenure) : 0;
    const totalDp = Math.round(dp);
    return { auto, dp, loan, emi, totalDp, tenure: finScheme.tenure };
  })();

  const save = async () => {
    if (!form.customer.name?.trim()) { toast.error("Customer name is required"); return; }
    if (!form.vehicle_model?.trim() && !form.vehicle_ref_id) { toast.error("Select a vehicle"); return; }
    if (form.finance_quote.enabled && !form.finance_quote.scheme_id) { toast.error("Pick a finance scheme for the EMI quote"); return; }
    setSaving(true);
    try {
      const body = {
        ...form,
        discount: Number(form.discount || 0),
        finance_quote: form.finance_quote.enabled
          ? { enabled: true, scheme_id: form.finance_quote.scheme_id,
              down_payment: form.finance_quote.down_payment !== "" ? Number(form.finance_quote.down_payment) : null }
          : { enabled: false },
      };
      const url = isNew ? "/ev-pricing/quotes" : `/ev-pricing/quotes/${initial.id}`;
      const method = isNew ? "post" : "patch";
      await api[method](url, body);
      toast.success(isNew ? "Quote created" : "Quote updated");
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-ink/40 flex justify-end" onClick={onClose}>
      <div className="bg-canvas w-full max-w-3xl h-full overflow-y-auto shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 bg-canvas border-b border-canvas-line px-6 py-4 flex justify-between items-center z-10">
          <div>
            <div className="text-xs font-mono uppercase tracking-widest text-ink-muted">EV Quotation</div>
            <div className="text-lg font-semibold">{isNew ? "New Quote" : initial.serial}</div>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-canvas-panel"><X size={16} /></button>
        </div>

        <div className="p-6 space-y-5">
          <SectionTitle title="Header" />
          <div className="grid grid-cols-2 gap-3">
            <Field label="Quote Date"><input type="date" value={form.quote_date} onChange={(e) => setForm({...form, quote_date: e.target.value})} className="input" /></Field>
            <Field label="Valid Until"><input type="date" value={form.valid_until} onChange={(e) => setForm({...form, valid_until: e.target.value})} className="input" /></Field>
            <Field label="Branch">
              <select value={form.branch_name} onChange={(e) => pickBranch(e.target.value)} className="input" data-testid="evq-branch"
                disabled={branchLocked}>
                <option value="">— select —</option>
                {branches.map((b) => <option key={b.id} value={(b.name || "").toUpperCase()}>{b.name}</option>)}
              </select>
              {branchLocked && <div className="text-[9px] font-mono text-ink-muted mt-0.5">Locked to your showroom</div>}
            </Field>
            <Field label="Branch Phone"><input value={form.branch_phone} onChange={(e) => setForm({...form, branch_phone: e.target.value})} className="input" /></Field>
            <div className="col-span-2">
              <Field label="Branch Address"><input value={form.branch_address} onChange={(e) => setForm({...form, branch_address: e.target.value})} className="input" /></Field>
            </div>
          </div>

          <SectionTitle title="Customer" />
          <div className="grid grid-cols-2 gap-3">
            <Field label="Name"><input data-testid="evq-cust-name" value={form.customer.name} onChange={(e) => setForm({...form, customer: {...form.customer, name: e.target.value}})} className="input" /></Field>
            <Field label="Phone"><input data-testid="evq-cust-phone" value={form.customer.phone} onChange={(e) => setForm({...form, customer: {...form.customer, phone: e.target.value}})} className="input" /></Field>
            <Field label="City"><input value={form.customer.city} onChange={(e) => setForm({...form, customer: {...form.customer, city: e.target.value}})} className="input" /></Field>
            <Field label="Address"><input value={form.customer.address || ""} onChange={(e) => setForm({...form, customer: {...form.customer, address: e.target.value}})} className="input" /></Field>
          </div>

          <SectionTitle title="Finance" />
          <div className="grid grid-cols-3 gap-3">
            <Field label="Financed?">
              <select value={form.finance.is_financed ? "YES" : "NO"} onChange={(e) => setForm({...form, finance: {...form.finance, is_financed: e.target.value === "YES"}})} className="input">
                <option>NO</option><option>YES</option>
              </select>
            </Field>
            <Field label="Financier">
              <select value={form.finance.financier} onChange={(e) => setForm({...form, finance: {...form.finance, financier: e.target.value}})} className="input">
                <option value="">— none —</option>
                {financiers.map((f) => <option key={f}>{f}</option>)}
              </select>
            </Field>
            <Field label="2Y Ext Warranty?">
              <select value={form.finance.extended_warranty_2y ? "YES" : "NO"} onChange={(e) => {
                const on = e.target.value === "YES";
                setForm((f) => ({
                  ...f,
                  finance: {...f.finance, extended_warranty_2y: on},
                  items: on ? f.items : { ...(f.items || {}), extended_warranty: 0 },
                }));
              }} className="input">
                <option>NO</option><option>YES</option>
              </select>
            </Field>
            <Field label="Include Finance Quote (EMI)?">
              <select
                value={form.finance_quote.enabled ? "YES" : "NO"}
                onChange={(e) => {
                  const on = e.target.value === "YES";
                  setForm((f) => ({
                    ...f,
                    finance_quote: { ...f.finance_quote, enabled: on },
                    finance: on ? { ...f.finance, is_financed: true } : f.finance,
                    items: on ? { ...f.items, hypothecation: f.items.hypothecation || 500 } : f.items,
                  }));
                }}
                data-testid="evq-finance-toggle"
                className="input">
                <option>NO</option><option>YES</option>
              </select>
            </Field>
          </div>

          {form.finance_quote.enabled && (
            <div className="border border-canvas-line p-3 space-y-3 bg-canvas-panel/30" data-testid="evq-finance-block">
              <div className="grid grid-cols-2 gap-3">
                <Field label="Finance Scheme">
                  <FinanceSchemePicker
                    schemes={finSchemes}
                    value={form.finance_quote.scheme_id}
                    onChange={(id) => setForm((f) => ({ ...f, finance_quote: { ...f.finance_quote, scheme_id: id, down_payment: "" } }))}
                    testId="evq-finance-scheme"
                  />
                </Field>
                <Field label={`Down Payment ₹${finPreview ? ` (auto ${inr(finPreview.auto)})` : ""}`}>
                  <input
                    type="number"
                    value={form.finance_quote.down_payment}
                    placeholder={finPreview ? String(finPreview.auto) : ""}
                    onChange={(e) => setForm((f) => ({ ...f, finance_quote: { ...f.finance_quote, down_payment: e.target.value } }))}
                    data-testid="evq-finance-dp"
                    className="input" />
                </Field>
              </div>
              {finPreview && (
                <div className="grid grid-cols-4 gap-3" data-testid="evq-finance-preview">
                  {[
                    ["Loan Amount", inr(finPreview.loan)],
                    [`EMI × ${finPreview.tenure}m`, inr(finPreview.emi)],
                    ["Down Payment", inr(finPreview.totalDp)],
                    ["Scheme", finScheme?.product_id || "—"],
                  ].map(([label, val], i) => (
                    <div key={label}>
                      <div className="text-[9px] font-mono uppercase tracking-widest text-ink-muted">{label}</div>
                      <div className={`font-mono font-semibold ${i === 1 ? "text-lg text-emerald-800" : "text-sm"}`}
                        data-testid={`evq-finance-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>{val}</div>
                    </div>
                  ))}
                </div>
              )}
              <div className="text-[9px] font-mono text-ink-muted">
                EMI is a rough calculation — the same block is printed on this quote's PDF.
              </div>
            </div>
          )}

          <SectionTitle title="Vehicle & Pricing — from Pricing Engine (live)" />
          <div className="border border-canvas-line">
            <div className="flex items-center gap-2 p-2 bg-canvas-panel/50 border-b border-canvas-line">
              <MagnifyingGlass size={12} className="text-ink-muted" />
              <input value={plSearch} onChange={(e) => setPlSearch(e.target.value)}
                placeholder="Search model in pricelist…" data-testid="evq-pl-search"
                className="flex-1 bg-transparent text-xs focus:outline-none" />
              <div className="text-[10px] font-mono text-ink-muted">
                {filteredPricelist.length} of {pricelist.length}
              </div>
            </div>
            <div className="flex flex-wrap gap-1 px-2 py-1.5 border-b border-canvas-line bg-canvas-panel/30">
              <button
                onClick={() => setPlCategory("")}
                data-testid="evq-pl-cat-all"
                className={`px-2 py-0.5 text-[10px] font-mono uppercase tracking-wider border ${!plCategory ? "bg-ink text-white border-ink" : "border-canvas-line text-ink-muted hover:border-ink"}`}
              >
                All
              </button>
              {categories.map((c) => (
                <button key={c}
                  onClick={() => setPlCategory(plCategory === c ? "" : c)}
                  data-testid={`evq-pl-cat-${c.replace(/\W+/g, "-").toLowerCase()}`}
                  className={`px-2 py-0.5 text-[10px] font-mono uppercase tracking-wider border ${plCategory === c ? "bg-ink text-white border-ink" : "border-canvas-line text-ink-muted hover:border-ink"}`}
                >
                  {c}
                </button>
              ))}
            </div>
            <div className="max-h-[220px] overflow-y-auto">
              <table className="w-full text-xs">
                <thead className="bg-canvas-panel sticky top-0">
                  <tr>
                    {["Model", "Subsidy", "Ex-Show", "Insurance", "Dealer", "On-Road", ""].map((h) =>
                      <th key={h} className="px-2 py-1 font-mono text-[9px] uppercase text-left">{h}</th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {filteredPricelist.length === 0 ? (
                    <tr><td colSpan={7} className="px-2 py-4 text-center text-ink-muted text-xs">
                      {pricelist.length ? "No matches — clear search" : "Loading pricelist…"}
                    </td></tr>
                  ) : filteredPricelist.map((p) => {
                    const active = form.vehicle_ref_id === p.id;
                    return (
                      <tr key={p.id}
                        onClick={() => pickVehicle(p.id)}
                        data-testid={`evq-pl-row-${p.id}`}
                        className={`border-t border-canvas-line/40 cursor-pointer ${active ? "bg-emerald-50" : "hover:bg-canvas-panel/40"}`}>
                        <td className="px-2 py-1 font-semibold">{p.package_name}</td>
                        <td className="px-2 py-1 font-mono text-[11px] text-right">{inr(p.subsidy)}</td>
                        <td className="px-2 py-1 font-mono text-[11px] text-right">{inr(p.ex_showroom)}</td>
                        <td className="px-2 py-1 font-mono text-[11px] text-right">{inr(p.insurance_wo_nildip)}</td>
                        <td className="px-2 py-1 font-mono text-[11px] text-right">{inr(p.dealer_price)}</td>
                        <td className="px-2 py-1 font-mono text-[11px] text-right font-semibold">{inr(p.on_road)}</td>
                        <td className="px-2 py-1 text-right">
                          {active
                            ? <span className="text-[9px] font-mono uppercase text-emerald-800">Selected</span>
                            : <span className="text-[9px] font-mono uppercase text-ink-muted">Click</span>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className="p-2 border-t border-canvas-line grid grid-cols-2 gap-3 bg-canvas-panel/30">
              <Field label="Selected Model"><input value={form.vehicle_model} readOnly className="input" /></Field>
              <Field label="Colour"><input value={form.vehicle_colour} onChange={(e) => setForm({...form, vehicle_colour: e.target.value})} className="input" /></Field>
            </div>
          </div>

          {form.vehicle_model && (
            <div className="border border-canvas-line">
              <table className="w-full text-xs">
                <thead className="bg-canvas-panel">
                  <tr>
                    <th className="px-2 py-1 text-left font-mono uppercase text-[10px]">S.NO</th>
                    <th className="px-2 py-1 text-left font-mono uppercase text-[10px]">Description</th>
                    <th className="px-2 py-1 text-right font-mono uppercase text-[10px]">Amount (₹)</th>
                  </tr>
                </thead>
                <tbody>
                  {LINE_ITEMS.map(([k, label, gate], idx) => {
                    if (gate && !form.finance?.extended_warranty_2y) return null;
                    return (
                    <tr key={k} className="border-t border-canvas-line/50">
                      <td className="px-2 py-1 font-mono text-[10px]">{idx + 1}</td>
                      <td className="px-2 py-1">{label}</td>
                      <td className="px-2 py-1">
                        <input type="number" value={form.items[k] ?? 0}
                          onChange={(e) => setItem(k, e.target.value)}
                          data-testid={`evq-item-${k}`}
                          className="w-32 px-2 py-0.5 text-right text-xs font-mono border border-canvas-line bg-canvas" />
                      </td>
                    </tr>
                    );
                  })}
                  <tr className="bg-canvas-panel/60 font-semibold border-t border-canvas-line">
                    <td colSpan={2} className="px-2 py-1 text-right font-mono uppercase text-[10px]">Subtotal</td>
                    <td className="px-2 py-1 text-right font-mono">{inr(totals.sub)}</td>
                  </tr>
                  <tr>
                    <td colSpan={2} className="px-2 py-1 text-right font-mono uppercase text-[10px] text-ink-muted">Discount</td>
                    <td className="px-2 py-1">
                      <input type="number" value={form.discount}
                        onChange={(e) => setForm({...form, discount: e.target.value === "" ? 0 : Number(e.target.value)})}
                        data-testid="evq-discount"
                        className="w-32 px-2 py-0.5 text-right text-xs font-mono border border-canvas-line bg-canvas" />
                    </td>
                  </tr>
                  <tr className="bg-emerald-50 font-bold">
                    <td colSpan={2} className="px-2 py-1 text-right font-mono uppercase text-[10px]">NET TOTAL</td>
                    <td className="px-2 py-1 text-right font-mono text-emerald-800" data-testid="evq-net">{inr(totals.net)}</td>
                  </tr>
                </tbody>
              </table>
              <div className="p-2 border-t border-canvas-line grid grid-cols-2 gap-3 bg-canvas-panel/30">
                <Field label="Cash Back Offer">
                  <input type="number" value={form.items.cash_back_offer ?? 0} onChange={(e) => setItem("cash_back_offer", e.target.value)} className="input" />
                </Field>
                <Field label="Card-Based Offer">
                  <input type="number" value={form.items.card_based_offer ?? 0} onChange={(e) => setItem("card_based_offer", e.target.value)} className="input" />
                </Field>
              </div>
            </div>
          )}

          <Field label="Notes">
            <textarea value={form.notes} onChange={(e) => setForm({...form, notes: e.target.value})} rows={2} className="input" />
          </Field>

          <div className="flex justify-end gap-2 pt-4 border-t border-canvas-line">
            <button onClick={onClose} className="px-4 py-2 border border-canvas-line text-xs font-mono uppercase hover:border-ink">Cancel</button>
            <button onClick={save} disabled={saving} data-testid="evq-save"
              className="px-4 py-2 bg-ink text-white text-xs font-mono uppercase tracking-wider hover:bg-brand disabled:opacity-50">
              {saving ? "Saving…" : (isNew ? "Create Quote" : "Save Changes")}
            </button>
          </div>
        </div>
      </div>
      <style>{`.input { width:100%; padding:6px 8px; background: var(--canvas, white); border:1px solid var(--canvas-line, #ddd); font-size: 12px; font-family: inherit; }`}</style>
    </div>
  );
}


function Field({ label, children }) {
  return (
    <div>
      <label className="block text-[10px] font-mono uppercase tracking-widest text-ink-muted mb-1">{label}</label>
      {children}
    </div>
  );
}

function SectionTitle({ title }) {
  return (
    <div className="pt-2 pb-1 border-b border-canvas-line font-mono text-[10px] uppercase tracking-widest text-ink-muted">
      {title}
    </div>
  );
}


/**
 * Read-only live EV Price List — computed on the fly from the Pricing
 * Engine on every load, so staff always see today's on-road numbers.
 * Category chips + search; a per-row "Quote" shortcut opens the New Quote
 * drawer pre-filled with that model.
 */
export function EvPriceListPanel({ onNewQuote }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [refreshedAt, setRefreshedAt] = useState(null);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/ev-pricing/quotes/pricelist");
      setRows(data || []);
      setRefreshedAt(new Date());
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const categories = useMemo(
    () => [...new Set(rows.map((r) => r.family || "Other"))],
    [rows]
  );

  const visible = useMemo(() => {
    const s = q.trim().toLowerCase();
    let list = rows;
    if (cat) list = list.filter((r) => (r.family || "Other") === cat);
    if (s) list = list.filter((r) => r.package_name.toLowerCase().includes(s));
    return list;
  }, [rows, q, cat]);

  return (
    <div className="space-y-3" data-testid="ev-pricelist-panel">
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[220px]">
          <MagnifyingGlass size={12} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-muted" />
          <input value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Search model…" data-testid="evpl-search"
            className="w-full pl-8 pr-3 py-2 bg-canvas border border-canvas-line text-sm focus:outline-none focus:border-ink" />
        </div>
        <button onClick={load} disabled={loading} data-testid="evpl-refresh"
          className="px-3 py-2 border border-canvas-line text-xs font-mono uppercase tracking-wider hover:border-ink flex items-center gap-1 disabled:opacity-50">
          <ArrowsClockwise size={12} weight="bold" className={loading ? "animate-spin" : ""} /> Refresh
        </button>
        <div className="ml-auto text-[10px] font-mono text-ink-muted">
          {refreshedAt ? `Live from Pricing Engine · refreshed ${refreshedAt.toLocaleTimeString()}` : "Loading…"}
        </div>
      </div>

      <div className="flex flex-wrap gap-1">
        <button onClick={() => setCat("")} data-testid="evpl-cat-all"
          className={`px-2.5 py-1 text-[10px] font-mono uppercase tracking-wider border ${!cat ? "bg-ink text-white border-ink" : "border-canvas-line text-ink-muted hover:border-ink"}`}>
          All ({rows.length})
        </button>
        {categories.map((c) => (
          <button key={c} onClick={() => setCat(cat === c ? "" : c)}
            data-testid={`evpl-cat-${c.replace(/\W+/g, "-").toLowerCase()}`}
            className={`px-2.5 py-1 text-[10px] font-mono uppercase tracking-wider border ${cat === c ? "bg-ink text-white border-ink" : "border-canvas-line text-ink-muted hover:border-ink"}`}>
            {c} ({rows.filter((r) => (r.family || "Other") === c).length})
          </button>
        ))}
      </div>

      <div className="border border-canvas-line overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-canvas-panel border-b border-canvas-line">
            <tr>
              {["Model", "Family", "Subsidy", "Ex-Showroom", "Insurance", "Dealer Price", "On-Road", "On-Road (Finance)", ""].map((h) => (
                <th key={h} className="px-3 py-2 font-mono text-[10px] uppercase text-left whitespace-nowrap">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {visible.length === 0 ? (
              <tr><td colSpan={9} className="px-3 py-8 text-center text-ink-muted text-xs">
                {loading ? "Loading pricelist…" : "No models match."}
              </td></tr>
            ) : visible.map((r) => (
              <tr key={r.id} className="border-b border-canvas-line/50 hover:bg-canvas-panel/40" data-testid={`evpl-row-${r.id}`}>
                <td className="px-3 py-2 text-xs font-semibold">{r.package_name}</td>
                <td className="px-3 py-2 font-mono text-[10px] uppercase text-ink-muted">{r.family}</td>
                <td className="px-3 py-2 font-mono text-xs text-right">{inr(r.subsidy)}</td>
                <td className="px-3 py-2 font-mono text-xs text-right">{inr(r.ex_showroom)}</td>
                <td className="px-3 py-2 font-mono text-xs text-right">{inr(r.insurance_wo_nildip)}</td>
                <td className="px-3 py-2 font-mono text-xs text-right">{inr(r.dealer_price)}</td>
                <td className="px-3 py-2 font-mono text-xs text-right font-semibold">{inr(r.on_road)}</td>
                <td className="px-3 py-2 font-mono text-xs text-right">{inr(r.on_road_finance)}</td>
                <td className="px-3 py-2 text-right">
                  {onNewQuote && (
                    <button onClick={() => onNewQuote(r)} data-testid={`evpl-quote-${r.id}`}
                      className="px-2 py-1 bg-ink text-white text-[10px] font-mono uppercase tracking-wider hover:bg-brand">
                      Quote
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
